import { Modal } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import { useChangePasswordMutation } from "Services/Admin/ChangePasswordApiSlice";
import {
  useGetSecurityConfigQuery,
  useSaveOrUpdateSecurityConfigMutation,
} from "Services/Admin/SecurityConfigApiSlice";
import { useAppDispatch } from "hooks";

interface SecurityFormData {
  isTwoFactorEnabled: boolean;
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioOutgoingSmsNumber: string;
}

interface StoredUserData {
  email?: string;
  authenticationId?: string;
  userId?: string;
}

const SecurityTab: React.FC = () => {
  const {
    data: securityConfig,
    isLoading,
    error,
    isError,
  } = useGetSecurityConfigQuery();

  const [changePassword, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();
  const [saveOrUpdateSecurityConfig] = useSaveOrUpdateSecurityConfigMutation();

  const [formData, setFormData] = useState<SecurityFormData>({
    isTwoFactorEnabled: false,
    twilioAccountSid: "",
    twilioAuthToken: "",
    twilioOutgoingSmsNumber: "",
  });

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const passwordSubmitLockRef = useRef(false);
  const dispatch = useAppDispatch();

  const getUserFromSession = (): StoredUserData | null => {
    try {
      const userStr = sessionStorage.getItem("adminUser");
      if (userStr) {
        return JSON.parse(userStr);
      }

      const localStorageUser = localStorage.getItem("adminUser");
      if (localStorageUser) {
        return JSON.parse(localStorageUser);
      }

      return null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  };

  useEffect(() => {
    if (securityConfig?.results) {
      setFormData({
        isTwoFactorEnabled: securityConfig.results.twoFactorEnabled || false,
        twilioAccountSid: securityConfig.results.twilioAccountSid || "",
        twilioAuthToken: securityConfig.results.twilioAuthToken || "",
        twilioOutgoingSmsNumber:
          securityConfig.results.twilioOutgoingSmsNumber || "",
      });
    }
  }, [securityConfig]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSavePassword = async () => {
 
  if (passwordSubmitLockRef.current || isChangingPassword) return;


  if (!passwordForm.currentPassword.trim()) {
    return;
  }

  if (!passwordForm.newPassword.trim()) {
    return;
  }

  if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
    return;
  }

  if (passwordForm.newPassword.length < 5) {
    return;
  }

  passwordSubmitLockRef.current = true; 

  try {
    const userData = getUserFromSession();

    if (!userData) {
      throw new Error("User session not found. Sign In again.");
    }

    const userId =
      userData.userId || userData.email || userData.authenticationId;

    if (!userId) {
      throw new Error("User email not found in session");
    }

    const changePasswordData = {
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
      dept: process.env.REACT_APP_PLANT || "ADMIN",
      userId: userId,
    };

    const result = await changePassword(changePasswordData).unwrap();

    notifications.show({
      id: "pwd-success",
      message: result.message,
      color: "green",
      icon: <IconCheck size={16} />,
    });

    setPasswordModalOpen(false);
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    });

    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);

  } catch (error: any) {
    const errorMessage =
      error?.data?.message ||
      error?.message ||
      "Failed to change password";

    notifications.show({
      id: "pwd-error",
      message: errorMessage,
      color: "red",
      icon: <IconX size={16} />,
    });
  } finally {
    passwordSubmitLockRef.current = false; 
  }
};

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="font-body tracking-wider space-y-8">
      <div className="flex flex-col lg:flex-row lg:justify-between items-start lg:items-center gap-2 border-b pb-2">
        <div>
          <h2 className="text-sm font-medium">Change Password</h2>
          <p className="text-xs text-gray-500 mt-1">
            Update your password to keep your account secure
          </p>
        </div>
        <button
          className={`px-4 py-2 text-sm text-white rounded-md bg-blue-600 hover:bg-blue-700 ${
            isChangingPassword ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={() => setPasswordModalOpen(true)}
          disabled={isChangingPassword}
        >
          {isChangingPassword ? "Updating..." : "Update"}
        </button>
      </div>

      <Modal
        opened={passwordModalOpen}
        onClose={() => {
          if (!isChangingPassword) {
            setPasswordModalOpen(false);
            setPasswordForm({
              currentPassword: "",
              newPassword: "",
              confirmNewPassword: "",
            });
            setShowCurrentPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
          }
        }}
        title="Change Password"
        size="md"
        centered
      >
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                name="currentPassword"
                className="w-full px-3 py-2 border rounded-md text-xs pr-10"
                placeholder="••••••••"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                disabled={isChangingPassword}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                disabled={isChangingPassword}
              >
                {showCurrentPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-500"
                  >
                    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                    <path d="m2 2 20 20" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-500"
                  >
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                className="w-full px-3 py-2 border rounded-md text-xs pr-10"
                placeholder="••••••••"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                disabled={isChangingPassword}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowNewPassword(!showNewPassword)}
                disabled={isChangingPassword}
              >
                {showNewPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-500"
                  >
                    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                    <path d="m2 2 20 20" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-500"
                  >
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmNewPassword"
                className="w-full px-3 py-2 border rounded-md text-xs pr-10"
                placeholder="••••••••"
                value={passwordForm.confirmNewPassword}
                onChange={handlePasswordChange}
                disabled={isChangingPassword}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isChangingPassword}
              >
                {showConfirmPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-500"
                  >
                    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                    <path d="m2 2 20 20" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-500"
                  >
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              className={`px-4 py-2 text-sm text-gray-700 border rounded-md hover:bg-gray-50 ${
                isChangingPassword ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={() => {
                if (!isChangingPassword) {
                  setPasswordModalOpen(false);
                  setPasswordForm({
                    currentPassword: "",
                    newPassword: "",
                    confirmNewPassword: "",
                  });
                  setShowCurrentPassword(false);
                  setShowNewPassword(false);
                  setShowConfirmPassword(false);
                }
              }}
              disabled={isChangingPassword}
            >
              Cancel
            </button>
            <button
  className={`px-4 py-2 text-sm text-white rounded-md flex items-center gap-2
    ${
      isChangingPassword
        ? "bg-blue-400 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700"
    }`}
  onClick={handleSavePassword}
  disabled={isChangingPassword}
>
  {isChangingPassword && (
    <svg
      className="w-4 h-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v8H4z"
      />
    </svg>
  )}
  {isChangingPassword ? "Saving..." : "Save Password"}
</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SecurityTab;
